import { useTranslations } from 'next-intl'
import { StudioButton } from '@repo/studio-components/studio-button'
import { ChevronRightIcon } from 'primitives/icons'
import * as Accordion from 'primitives/accordion'
import { Divider } from 'components/divider'
import { HamburgerMenu as HamburgerMenuComponent } from 'components/hamburger-menu'
import { AppRoutes } from '@repo/web-utils/app-routes'
import { Link } from '@repo/web-ui/components/link'
import { tw } from 'utils/classnames'

import { NavigationLink, NavigationLinkWithIcon } from '@web/components/navigation/navigation-items'
import { useMenuItems, MenuItemType } from '@web/hooks/use-menu-items'

const accordionIcon = (
  <ChevronRightIcon
    className="h-[16px] w-[16px] text-gray-500 transition-transform group-data-[state='open']/trigger:rotate-180"
    aria-hidden
  />
)

export function HamburgerMenu() {
  const t = useTranslations('components.studio_header')
  const menuItems = useMenuItems()

  return (
    <HamburgerMenuComponent triggerClassName="text-[var(--navigation-menu-hamburger-icon-color)] hover:text-[var(--navigation-menu-hamburger-icon-color)]">
      <nav
        role="navigation"
        aria-label="Primary Navigation"
        style={{ '--scroller-gutter': '20px' }}
        className="scroller-xs flex w-full flex-col justify-between py-5"
      >
        <ul className="flex w-full flex-col">
          {menuItems.map(item => {
            if (item.type !== MenuItemType.NO_CHILDREN) {
              return (
                <Accordion.Root key={item.name} type="single" collapsible>
                  <Accordion.Item value={item.name}>
                    <Accordion.Header>
                      <Accordion.Trigger
                        className="w-full justify-between text-gray-500 hover:text-gray-50 focus:text-gray-50"
                        icon={accordionIcon}
                      >
                        {item.name}
                      </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Content
                      className={tw(
                        "data-[state='closed']/item:animate-none data-[state='open']/item:animate-none",
                        "md:data-[state='open']/item:animate-height-in md:data-[state='closed']/item:animate-height-out",
                        '-mx-2 px-2',
                      )}
                    >
                      <Divider className="mb-2 mt-1 bg-gray-800" />
                      {(item.type === MenuItemType.LINKS_AND_IMAGES ||
                        item.type === MenuItemType.LINKS_ONLY) &&
                      item?.mobileShowAllName ? (
                        <NavigationLink
                          name={
                            <div className="flex w-full items-center justify-between">
                              <span>{item.mobileShowAllName}</span>
                              {accordionIcon}
                            </div>
                          }
                          href={item.href}
                          linkClassName="w-full"
                        />
                      ) : null}
                      <ul className="mb-4 flex flex-col gap-1">
                        {item.type === MenuItemType.LINKS_ONLY
                          ? item.subMenuItems.map(subItem => (
                              <NavigationLink
                                key={subItem.name}
                                name={subItem.name}
                                href={subItem.href}
                              />
                            ))
                          : null}

                        {item.type === MenuItemType.LINKS_AND_IMAGES
                          ? item.subMenuItems.map(subItem => (
                              <NavigationLinkWithIcon
                                key={subItem.name}
                                name={subItem.name}
                                href={subItem.href}
                                image={subItem.image}
                                subtitle={subItem.subtitle}
                              />
                            ))
                          : null}

                        {item.type === MenuItemType.CUSTOM ? item.content : null}
                      </ul>
                    </Accordion.Content>
                  </Accordion.Item>
                </Accordion.Root>
              )
            }

            return (
              <a className="flex h-10 items-center text-gray-50" key={item.name} href={item.href}>
                {item.name}
              </a>
            )
          })}
        </ul>
        <div className="mt-5 flex flex-col gap-2">
          <StudioButton
            asChild
            variant="invisible"
            className="h-[42px] w-full justify-center border border-gray-800"
          >
            <Link target="_blank" href={AppRoutes.STUDIO_TOOL}>
              {t('buttons.my_projects')}
            </Link>
          </StudioButton>
          <StudioButton asChild className="h-[42px] w-full justify-center">
            <Link target="_blank" href={AppRoutes.STUDIO_TOOL_CREATE}>
              {t('buttons.create_a_design')}
            </Link>
          </StudioButton>
        </div>
      </nav>
    </HamburgerMenuComponent>
  )
}
